import { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { safeNextPath } from '@affily/shared/redirect';
import { useAuth } from '../hooks/useAuth.jsx';
import { rule } from '../lib/fieldRules.js';
import { Seo } from '../components/Seo.jsx';
import { SignInButtons } from '../components/SignInButtons.jsx';
import { api } from '../lib/api.js';
import { Button, Field, Input, Alert } from '../components/ui.jsx';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const { login } = useAuth();

  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // Return the user where they were headed: back through the auth wall, or on
  // from a signup page that sent them here. `?next=` is attacker-controllable,
  // so it only counts when it is a path on this site.
  const queryNext = safeNextPath(params.get('next'));
  const next = location.state?.from ?? queryNext ?? '/dashboard';
  const signupHref = queryNext ? `/signup?next=${encodeURIComponent(queryNext)}` : '/signup';

  // Back from Facebook or Instagram without being signed in: say why. The
  // reason comes from the server, never from the address bar.
  const failed = params.get('failed') === '1';
  useEffect(() => {
    if (!failed) return;
    api
      .get('/auth/oauth/status')
      .then((s) => s?.error && setError(s.error))
      .catch(() => {});
  }, [failed]);

  async function onSubmit(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await login(form.email, form.password);
      navigate(next, { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Seo title="Sign in" noindex />
      <div className="mx-auto flex min-h-dvh max-w-md flex-col justify-center px-6 py-12">
        <Link to="/" className="text-2xs font-semibold uppercase tracking-widest text-accent">
          Affily
        </Link>
        <h1 className="mt-2 text-3xl font-semibold tracking-tight">Sign in</h1>
        <p className="mt-1.5 text-sm text-ink-muted">
          Browsing programs and creators never needs an account.
        </p>

        <div className="mt-6">
          <SignInButtons next={next} onSuccess={() => navigate(next, { replace: true })} onError={setError} />
        </div>

        <form onSubmit={onSubmit} className="mt-4 space-y-4">
          <Alert>{error}</Alert>

          {/* "username", not "email": it is the field Safari (and other password
              managers) matches the saved login against. */}
          <Field label="Email" required constraint={rule.email()}>
            <Input
              type="email"
              name="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              autoComplete="username"
              required
            />
          </Field>

          <Field label="Password" required>
            <Input
              type="password"
              name="password"
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              autoComplete="current-password"
              required
            />
          </Field>

          <p className="-mt-2 text-right text-sm">
            <Link to="/forgot-password" className="text-ink-muted hover:text-accent hover:underline">
              Forgot your password?
            </Link>
          </p>

          <Button type="submit" size="lg" className="w-full" disabled={busy}>
            {busy ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-ink-muted">
          New here?{' '}
          <Link to={signupHref} className="font-medium text-accent hover:underline">
            Join free
          </Link>
        </p>
      </div>
    </>
  );
}
